import { useCallback, useEffect, useState } from 'react';
import { Room } from 'livekit-client';

export interface DeviceLists {
  cameras: MediaDeviceInfo[];
  mics: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
}

const EMPTY: DeviceLists = { cameras: [], mics: [], speakers: [] };

/**
 * Список доступных устройств. До выдачи разрешения браузер отдаёт записи
 * с пустыми label — поэтому обновляем список после получения доступа
 * и по событию `devicechange` (наушники подключили посреди встречи).
 */
export function useDevices(enabled = true): DeviceLists & { refresh: () => void } {
  const [devices, setDevices] = useState<DeviceLists>(EMPTY);

  const refresh = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    try {
      const [cameras, mics, speakers] = await Promise.all([
        Room.getLocalDevices('videoinput', true),
        Room.getLocalDevices('audioinput', true),
        // Для устройств вывода разрешения не запрашиваем: лишний запрос
        // ничего не даст, а в части браузеров вызывает повторный промпт.
        Room.getLocalDevices('audiooutput', false).catch(() => []),
      ]);
      setDevices({ cameras, mics, speakers });
    } catch {
      setDevices(EMPTY);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh();

    navigator.mediaDevices?.addEventListener?.('devicechange', refresh);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', refresh);
  }, [enabled, refresh]);

  return { ...devices, refresh };
}

/** Понятное имя устройства, когда браузер не отдал label. */
export function deviceLabel(device: MediaDeviceInfo, index: number, kind: string): string {
  if (device.label) return device.label;
  return `${kind} ${index + 1}`;
}
